import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { collection, getDocs, getFirestore, query, where } from '@firebase/firestore';
import { app } from '../../firebase.js';
import LeftBar from '../../Components/LeftBar';
import Dashnav from '../../Components/Dashnav';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import Table from 'react-bootstrap/Table';
import Badge from 'react-bootstrap/Badge';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowsRotate,
  faMagnifyingGlass,
  faUserCheck,
  faUserClock,
  faUsers
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import defaultProfilePicture from '../../Images/defprofile.png';
import { toDateValue } from '../../utils/financeWorkflow';

const ATTENDANCE_COLLECTION = 'AttendaceReport';
const SCHOOL_SESSION_TITLE = 'School Check-In';
const NAIROBI_TIME_ZONE = 'Africa/Nairobi';
const DAY_IN_MS = 24 * 60 * 60 * 1000;

const WINDOW_OPTIONS = [
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: 'all', label: 'All time' }
];

const ACTIVITY_OPTIONS = ['All', 'Active today', 'Active this week', 'Idle', 'Dormant'];
const CHANNEL_OPTIONS = ['All', 'Physical', 'Virtual', 'Both'];

const normalizeText = (value) => String(value || '').trim().toLowerCase();

const formatDateInputValue = (value = new Date()) => {
  const dateValue = toDateValue(value) || new Date();
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: NAIROBI_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(dateValue);
};

const getDateKey = (value) => {
  const dateValue = toDateValue(value);
  if (!dateValue) {
    return '';
  }

  return new Intl.DateTimeFormat('en-CA', {
    timeZone: NAIROBI_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(dateValue);
};

const formatDateTime = (value, fallback = '—') => {
  const dateValue = toDateValue(value);
  if (!dateValue) {
    return fallback;
  }

  return dateValue.toLocaleString('en-KE', {
    timeZone: NAIROBI_TIME_ZONE,
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
};

const formatRelativeTime = (value) => {
  const dateValue = toDateValue(value);
  if (!dateValue) {
    return 'No recent activity';
  }

  const daysSince = Math.floor((Date.now() - dateValue.getTime()) / DAY_IN_MS);
  if (daysSince <= 0) {
    return 'Today';
  }

  if (daysSince === 1) {
    return '1 day ago';
  }

  return `${daysSince} days ago`;
};

const normalizeAttendanceType = (record = {}) => {
  const sessionTitle = normalizeText(record.sessionTitle || record.className);
  const mode = normalizeText(record.mode || record.attendanceMode);
  const hasRoomLink = Boolean(String(record.roomlink || '').trim());
  const hasVirtualSignals = hasRoomLink
    || mode === 'online'
    || sessionTitle.includes('live')
    || sessionTitle.includes('virtual')
    || Boolean(record.startTime || record.endTime);
  const hasPhysicalSignals = sessionTitle === normalizeText(SCHOOL_SESSION_TITLE)
    || sessionTitle.includes('school check')
    || mode === 'onsite'
    || Boolean(record.locationCheck);

  if (hasVirtualSignals && !hasPhysicalSignals) {
    return 'Virtual';
  }

  if (hasPhysicalSignals) {
    return 'Physical';
  }

  return null;
};

const getActivityLevel = (value) => {
  const dateValue = toDateValue(value);
  if (!dateValue) {
    return 'Dormant';
  }

  const daysSince = Math.floor((Date.now() - dateValue.getTime()) / DAY_IN_MS);

  if (daysSince <= 1) {
    return 'Active today';
  }

  if (daysSince <= 7) {
    return 'Active this week';
  }

  if (daysSince <= 30) {
    return 'Idle';
  }

  return 'Dormant';
};

const getActivityTone = (label) => {
  if (label === 'Active today') {
    return 'activityLevelBadge activityLevelBadge--today';
  }

  if (label === 'Active this week') {
    return 'activityLevelBadge activityLevelBadge--week';
  }

  if (label === 'Idle') {
    return 'activityLevelBadge activityLevelBadge--idle';
  }

  return 'activityLevelBadge activityLevelBadge--dormant';
};

const pickLatestRecord = (records = []) => {
  return [...records].sort((left, right) => {
    const leftTime = left?.checkInTime?.getTime?.() || left?.checkOutTime?.getTime?.() || 0;
    const rightTime = right?.checkInTime?.getTime?.() || right?.checkOutTime?.getTime?.() || 0;
    return rightTime - leftTime;
  })[0] || null;
};

const uniqueRecords = (records = []) => {
  return Array.from(new Map(records.map((record) => [record.id, record])).values());
};

const isWithinWindow = (record, windowValue) => {
  const referenceDate = record?.checkInTime || record?.checkOutTime;
  if (!referenceDate) {
    return false;
  }

  if (windowValue === 'all') {
    return true;
  }

  const daysLimit = windowValue === '7d' ? 7 : 30;
  const daysSince = Math.floor((Date.now() - referenceDate.getTime()) / DAY_IN_MS);
  return daysSince <= daysLimit;
};

function ActiveAttendanceStudents() {
  const db = getFirestore(app);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [windowFilter, setWindowFilter] = useState('30d');
  const [activityFilter, setActivityFilter] = useState('All');
  const [channelFilter, setChannelFilter] = useState('All');
  const [students, setStudents] = useState([]);
  const [attendanceRecords, setAttendanceRecords] = useState([]);

  const fetchActiveStudents = useCallback(async () => {
    setRefreshing(true);

    try {
      const [usersSnapshot, attendanceSnapshot] = await Promise.all([
        getDocs(query(collection(db, 'users'), where('userStatus', '==', 'Student'))),
        getDocs(collection(db, ATTENDANCE_COLLECTION))
      ]);

      const studentRows = [];
      usersSnapshot.forEach((docItem) => {
        const data = docItem.data();
        const userType = normalizeText(data.userType);
        const userStatus = normalizeText(data.userStatus);

        if (userType !== 'student' && userStatus !== 'student') {
          return;
        }

        studentRows.push({
          id: docItem.id,
          userId: data.userId || docItem.id,
          bio: data.bio || data.displayName || data.name || 'Unnamed student',
          course: data.course || 'Course not set',
          email: data.userEmail || data.email || '',
          phoneNumber: data.phoneNumber || data.phone || '',
          profilePic: data.profilePic || defaultProfilePicture,
          branchName: data.branchName || data.campus || data.selectedCampus || '',
          admissionNumber: data.admissionNumber || data.prospectAdmissionNumber || '',
          status: data.userStatus || data.userType || 'Student'
        });
      });

      const normalizedRecords = attendanceSnapshot.docs
        .map((docItem) => {
          const data = docItem.data();
          const checkInTime = toDateValue(data.signInTime || data.startTime || data.createdAt);
          const checkOutTime = toDateValue(data.signOutTime || data.endTime);
          const sessionTitle = data.sessionTitle || data.className || 'Class Session';
          const mode = data.mode || data.attendanceMode || (data.roomlink ? 'Online' : 'Onsite');
          const studentId = data.userId || data.studentId || '';
          const attendanceType = normalizeAttendanceType(data);

          if (!attendanceType || !studentId || !checkInTime) {
            return null;
          }

          return {
            id: docItem.id,
            studentId,
            studentName: data.username || data.studentName || 'Student',
            sessionTitle,
            mode,
            attendanceType,
            checkInTime,
            checkOutTime,
            isActive: Boolean(checkInTime && !checkOutTime),
            locationCheck: data.locationCheck || {},
            locationVerified: Boolean(
              data.locationCheck
              && Number.isFinite(Number(data.locationCheck.distanceInMeters))
              && Number(data.locationCheck.distanceInMeters) <= Number(data.locationCheck.radiusInMeters || 200)
            ),
            raw: data
          };
        })
        .filter(Boolean);

      setStudents(studentRows);
      setAttendanceRecords(normalizedRecords);
    } catch (error) {
      console.error('Failed to load active attendance students:', error);
      setStudents([]);
      setAttendanceRecords([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [db]);

  useEffect(() => {
    fetchActiveStudents();
  }, [fetchActiveStudents]);

  const activeStudents = useMemo(() => {
    const rowsByStudent = new Map();

    attendanceRecords.forEach((record) => {
      if (!isWithinWindow(record, windowFilter)) {
        return;
      }

      if (!rowsByStudent.has(record.studentId)) {
        rowsByStudent.set(record.studentId, []);
      }

      rowsByStudent.get(record.studentId).push(record);
    });

    return students
      .map((student) => {
        const studentRecords = uniqueRecords([
          ...(rowsByStudent.get(student.userId) || []),
          ...(rowsByStudent.get(student.id) || [])
        ]);

        if (studentRecords.length === 0) {
          return null;
        }

        const physicalRecords = studentRecords.filter((record) => record.attendanceType === 'Physical');
        const virtualRecords = studentRecords.filter((record) => record.attendanceType === 'Virtual');
        const lastActivity = pickLatestRecord(studentRecords);
        const lastAttendanceType = lastActivity?.attendanceType || 'Physical';
        const activityLevel = getActivityLevel(lastActivity?.checkInTime || lastActivity?.checkOutTime);

        return {
          ...student,
          studentRecords,
          physicalRecords,
          virtualRecords,
          totalSessions: studentRecords.length,
          lastActivity,
          lastAttendanceType,
          activityLevel,
          activityTime: lastActivity?.checkInTime || lastActivity?.checkOutTime || null,
          hasPhysical: physicalRecords.length > 0,
          hasVirtual: virtualRecords.length > 0,
          joinLabel: physicalRecords.length > 0 && virtualRecords.length > 0
            ? 'Physical + Virtual'
            : physicalRecords.length > 0
              ? 'Physical'
              : 'Virtual',
          sortTime: lastActivity?.checkInTime?.getTime?.() || lastActivity?.checkOutTime?.getTime?.() || 0,
          sortName: (student.bio || '').toLowerCase()
        };
      })
      .filter(Boolean)
      .filter((student) => {
        if (activityFilter !== 'All' && student.activityLevel !== activityFilter) {
          return false;
        }

        if (channelFilter === 'Physical' && !student.hasPhysical) {
          return false;
        }

        if (channelFilter === 'Virtual' && !student.hasVirtual) {
          return false;
        }

        if (channelFilter === 'Both' && !(student.hasPhysical && student.hasVirtual)) {
          return false;
        }

        if (windowFilter !== 'all' && !student.lastActivity) {
          return false;
        }

        return true;
      })
      .sort((first, second) => {
        const timeDelta = (second.sortTime || 0) - (first.sortTime || 0);
        if (timeDelta !== 0) {
          return timeDelta;
        }

        return first.sortName.localeCompare(second.sortName);
      });
  }, [activityFilter, attendanceRecords, channelFilter, students, windowFilter]);

  const filteredStudents = useMemo(() => {
    const queryText = searchTerm.trim().toLowerCase();

    if (!queryText) {
      return activeStudents;
    }

    return activeStudents.filter((student) => {
      const haystack = [
        student.bio,
        student.course,
        student.email,
        student.phoneNumber,
        student.admissionNumber,
        student.joinLabel,
        student.activityLevel,
        student.lastActivity?.sessionTitle,
        student.lastActivity?.mode
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return haystack.includes(queryText);
    });
  }, [activeStudents, searchTerm]);

  const stats = useMemo(() => {
    const todayKey = formatDateInputValue();
    const today = activeStudents.filter((student) => getDateKey(student.activityTime) === todayKey).length;
    const thisWeek = activeStudents.filter((student) => student.activityLevel === 'Active this week').length;
    const physical = activeStudents.filter((student) => student.hasPhysical).length;
    const virtual = activeStudents.filter((student) => student.hasVirtual).length;
    const both = activeStudents.filter((student) => student.hasPhysical && student.hasVirtual).length;

    return {
      activeStudents: activeStudents.length,
      today,
      thisWeek,
      physical,
      virtual,
      both
    };
  }, [activeStudents]);

  const latestActiveStudent = useMemo(() => activeStudents[0] || null, [activeStudents]);

  const handleViewStudent = useCallback((studentId) => {
    if (!studentId) {
      return;
    }

    navigate('/view-student', { state: { myData: studentId } });
  }, [navigate]);

  return (
    <div className="mainContainer">
      <LeftBar />
      <div className="theMainContainer">
        <Dashnav />
        <div className="clientsComp applicationsPage attendanceRegisterPage activeStudentsPage">
          <section className="appsHeader">
            <div className="attendanceHeaderCopy">
              <p className="appsKicker">Data Analytics</p>
              <h4>Active Attendance Students</h4>
              <p className="appsSubtext">
                Students with at least one physical or virtual attendance record in the selected window.
                Use this page to see who has been showing up most recently, and how they are joining.
              </p>
              <div className="attendanceHeaderTags">
                <span className="attendanceHeaderTag">Recent activity</span>
                <span className="attendanceHeaderTag">Physical joins</span>
                <span className="attendanceHeaderTag">Virtual joins</span>
              </div>
            </div>
            <div className="appsHeaderStats">
              <div className="appsStatChip">
                <span>Active students</span>
                <strong>{stats.activeStudents}</strong>
              </div>
              <div className="appsStatChip">
                <span>Today</span>
                <strong>{stats.today}</strong>
              </div>
              <div className="appsStatChip">
                <span>This week</span>
                <strong>{stats.thisWeek}</strong>
              </div>
              <div className="appsStatChip">
                <span>Physical</span>
                <strong>{stats.physical}</strong>
              </div>
              <div className="appsStatChip">
                <span>Virtual</span>
                <strong>{stats.virtual}</strong>
              </div>
            </div>
          </section>

          <section className="appsFiltersCard attendanceFiltersCard">
            <div className="appsSearch input-group attendanceSearch">
              <span className="input-group-text">
                <FontAwesomeIcon icon={faMagnifyingGlass} />
              </span>
              <Form.Control
                type="search"
                placeholder="Search student, course, phone, or admission number..."
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
              />
            </div>

            <div className="attendanceToolbarRow">
              <Form.Group className="attendanceTypeControl">
                <Form.Label>Activity window</Form.Label>
                <Form.Select
                  className="filterSelect"
                  value={windowFilter}
                  onChange={(event) => setWindowFilter(event.target.value)}
                >
                  {WINDOW_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="attendanceTypeControl">
                <Form.Label>Activity level</Form.Label>
                <Form.Select
                  className="filterSelect"
                  value={activityFilter}
                  onChange={(event) => setActivityFilter(event.target.value)}
                >
                  {ACTIVITY_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="attendanceTypeControl">
                <Form.Label>Join type</Form.Label>
                <div className="attendanceTypeToggle">
                  {CHANNEL_OPTIONS.map((option) => (
                    <button
                      key={option}
                      type="button"
                      className={`attendanceTypeButton ${channelFilter === option ? 'attendanceTypeButton--active' : ''}`}
                      onClick={() => setChannelFilter(option)}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </Form.Group>
            </div>

            <div className="incomeToolbarMeta attendanceMeta">
              <span className="incomeToolbarText">{filteredStudents.length} visible students</span>
              <span className="incomeInlineDot" />
              <span className="incomeToolbarText">Based on AttendaceReport activity</span>
            </div>

            <Button variant="outline-dark" onClick={fetchActiveStudents} disabled={refreshing}>
              <FontAwesomeIcon icon={faArrowsRotate} className="me-2" />
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
          </section>

          <section className="attendanceMiniSummary">
            <div className="attendanceMiniSummaryItem">
              <span>Active students</span>
              <strong>{stats.activeStudents}</strong>
            </div>
            <div className="attendanceMiniSummaryItem">
              <span>Latest window</span>
              <strong>{WINDOW_OPTIONS.find((option) => option.value === windowFilter)?.label || 'Window'}</strong>
            </div>
            <div className="attendanceMiniSummaryItem">
              <span>Both channels</span>
              <strong>{stats.both}</strong>
            </div>
            <div className="attendanceMiniSummaryItem">
              <span>Recent today</span>
              <strong>{stats.today}</strong>
            </div>
          </section>

          <div className="attendanceLatestNote">
            {latestActiveStudent
              ? `Latest activity: ${latestActiveStudent.bio || latestActiveStudent.studentName || 'Student'} ${formatRelativeTime(latestActiveStudent.activityTime)}`
              : 'No active attendance activity was found for the selected window.'}
          </div>

          {loading ? (
            <div className="incomeStateCard">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading active students...</span>
              </Spinner>
              <p>Loading active attendance students...</p>
            </div>
          ) : (
            <section className="appsTableCard attendanceTableCard">
              <div className="appsTableHeader">
                <h6>
                  <FontAwesomeIcon icon={faUsers} className="me-2" />
                  Active students
                </h6>
                <span>{filteredStudents.length} results</span>
              </div>

              <div className="appsTableWrap attendanceTableWrap">
                <Table hover responsive className="appsTable attendanceTable">
                  <thead>
                    <tr>
                      <th>Student</th>
                      <th>Course</th>
                      <th>Last activity</th>
                      <th>Level</th>
                      <th>Physical</th>
                      <th>Virtual</th>
                      <th>Sessions</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredStudents.map((student) => (
                      <tr key={student.userId || student.id}>
                        <td>
                          <div className="appsStudentCell attendanceStudentCell">
                            <div className="appDefProfPic attendanceStudentAvatar">
                              <img src={student.profilePic || defaultProfilePicture} alt={student.bio} />
                            </div>
                            <div>
                              <p className="appsStudentName">{student.bio}</p>
                              <span className="appsStudentMeta">
                                {student.email || student.userId}
                                {student.admissionNumber ? ` • ${student.admissionNumber}` : ''}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td>{student.course}</td>
                        <td>
                          <div className="attendanceRecordCell">
                            <strong>{formatDateTime(student.activityTime)}</strong>
                            <span>{student.lastActivity?.sessionTitle || 'Attendance session'}</span>
                          </div>
                        </td>
                        <td>
                          <Badge bg="light" text="dark" className={getActivityTone(student.activityLevel)}>
                            <FontAwesomeIcon
                              icon={student.activityLevel === 'Active today' ? faUserCheck : faUserClock}
                              className="me-2"
                            />
                            {student.activityLevel}
                          </Badge>
                        </td>
                        <td>
                          <span className="attendanceTypeBadge attendanceTypeBadge--physical">
                            {student.physicalRecords.length}
                          </span>
                        </td>
                        <td>
                          <span className="attendanceTypeBadge attendanceTypeBadge--virtual">
                            {student.virtualRecords.length}
                          </span>
                        </td>
                        <td>
                          <div className="attendanceRecordCell">
                            <strong>{student.totalSessions}</strong>
                            <span>{student.joinLabel}</span>
                          </div>
                        </td>
                        <td>
                          <button
                            type="button"
                            className="appsViewBtn"
                            onClick={() => handleViewStudent(student.userId || student.id)}
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                    {filteredStudents.length === 0 && (
                      <tr>
                        <td colSpan="8" className="appsEmptyState">
                          No active students match the selected filters.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

export default ActiveAttendanceStudents;
